/**
 * Editorial Split — Bone & Tobacco
 *
 * Archetype: a hard vertical split — narrow type column on warm paper, tall
 * full-bleed photograph beside it. Specs are set as a ruled list rather than a
 * stat row, which is what a narrow measure wants and what makes the page read
 * like a property brochure instead of an advert.
 */

import { CANVAS, SPACE, TYPE, RULE } from '../../system/tokens';
import { getPalette, scrim } from '../../system/palettes';
import { roles } from '../../system/typography';
import { text, shape, rule, image, page } from '../../system/elements';
import { PHOTOS } from '../../system/photos';

const ID = 'tpl-flyer-real-estate-02';

export default {
  id: ID,
  meta: {
    name: 'Editorial Split — Bone & Tobacco',
    collection: 'signature',
    archetype: 'vertical-split',
    documentType: 'Flyer',
    industry: 'Real Estate',
    subCategory: 'Real Estate',
    canvas: CANVAS.usLetterPortrait,
    palette: 'bone-editorial',
    pairing: 'cormorant-jost',
    tags: ['real estate', 'flyer', 'editorial', 'listing', 'minimal', 'brochure', 'premium', 'us letter'],
    description: 'A hard vertical split: serif type column on warm paper against a full-height property photograph, with specs set as a ruled list.',
  },

  build() {
    const canvas = CANVAS.usLetterPortrait;
    const p = getPalette('bone-editorial');
    const r = roles('cormorant-jost');
    const els = [];

    const SPLIT = 1440;          // photograph starts here
    const M = 190;               // type-column margin
    const COL = SPLIT - M - 150; // measure of the type column

    // ── Full-height photograph on the right ─────────────────────────────────
    els.push(image(`${ID}-hero`, {
      x: SPLIT, y: 0, width: canvas.width - SPLIT, height: canvas.height, src: PHOTOS.poolsideVilla,
    }));

    // Accent seam between field and photograph.
    els.push(shape(`${ID}-seam`, {
      x: SPLIT - RULE.medium, y: 0, width: RULE.medium, height: canvas.height, fill: p.accent,
    }));

    // ── Type column ─────────────────────────────────────────────────────────
    let y = 300;

    const brand = text(`${ID}-brand`, r.eyebrow, 'Harbour & Vine', { x: M, y, width: COL }, { color: p.accent });
    els.push(brand);
    y += brand.height + SPACE.xl;

    const kicker = text(`${ID}-kicker`, r.caption, 'For Sale · Beacon Hill, Boston', { x: M, y, width: COL }, { color: p.inkMuted });
    els.push(kicker);
    y += kicker.height + SPACE.sm;

    const headline = text(`${ID}-headline`, r.h1, 'A Townhouse\nof Rare\nProportion', {
      x: M, y, width: COL,
    }, { color: p.ink, fontSize: 200 });
    els.push(headline);
    y += headline.height + SPACE.md;

    els.push(rule(`${ID}-rule`, { x: M, y, width: 260, weight: RULE.medium, color: p.accent }));
    y += RULE.medium + SPACE.md;

    const blurb = text(`${ID}-blurb`, r.body,
      'Four storeys of restored brick and\nplaster on a quiet gas-lit street, with\na walled garden and south-facing rooms.', {
        x: M, y, width: COL,
      }, { color: p.inkMuted });
    els.push(blurb);
    y += blurb.height + SPACE.xl;

    // ── Ruled specification list ────────────────────────────────────────────
    const specs = [
      ['Bedrooms', 'Four'],
      ['Bathrooms', 'Three and a half'],
      ['Interior', '3,140 sq ft'],
      ['Built', '1874, restored 2021'],
    ];

    specs.forEach(([label, value], i) => {
      els.push(shape(`${ID}-spec${i}-rule`, {
        x: M, y, width: COL, height: RULE.hair, fill: scrim(p.ink, 0.25),
      }));
      const rowY = y + SPACE.xs;
      // Label and value are set on a shared optical centre, not a shared top:
      // the label is smaller, so it is nudged down by half the leading
      // difference. The 6% gap between the two columns keeps their boxes clear
      // of each other once TEXT_INSET widens both.
      const labelDrop = Math.round((TYPE.body * 1.5 - TYPE.caption * 1.4) / 2);
      els.push(text(`${ID}-spec${i}-label`, r.statLabel, label, { x: M, y: rowY + labelDrop, width: COL * 0.42 }, { color: p.inkMuted }));
      els.push(text(`${ID}-spec${i}-value`, r.body, value, { x: M + COL * 0.48, y: rowY, width: COL * 0.52, align: 'right' }, { color: p.ink }));
      y = rowY + TYPE.body * 1.5 + SPACE.xs;
    });

    els.push(shape(`${ID}-spec-rule-last`, { x: M, y, width: COL, height: RULE.hair, fill: scrim(p.ink, 0.25) }));
    y += SPACE.xl;

    // ── Price ───────────────────────────────────────────────────────────────
    const priceLabel = text(`${ID}-price-label`, r.statLabel, 'Offered at', { x: M, y, width: COL }, { color: p.inkMuted });
    els.push(priceLabel);
    y += priceLabel.height + SPACE.xxs;

    const price = text(`${ID}-price`, r.h1, '$3,150,000', { x: M, y, width: COL }, { color: p.ink, fontSize: 150 });
    els.push(price);

    // ── Footer, pinned to the baseline of the column ────────────────────────
    const footY = canvas.height - 420;
    els.push(shape(`${ID}-foot-rule`, { x: M, y: footY, width: COL, height: RULE.hair, fill: scrim(p.ink, 0.35) }));
    els.push(text(`${ID}-agent`, r.h3, 'Marielle Vance', { x: M, y: footY + SPACE.sm, width: COL }, { color: p.ink }));
    els.push(text(`${ID}-contact`, r.caption, '(617) 555-0182  ·  marielle@harbourvine.com\nharbourvine.com', {
      x: M, y: footY + SPACE.sm + TYPE.h3 * 1.25 + SPACE.xxs, width: COL,
    }, { color: p.inkMuted }));

    return {
      canvas,
      pages: [page(`${ID}-page-1`, { name: 'Flyer', background: p.bg, elements: els })],
    };
  },
};
